/**
 * Team Members Page (HTML clients only)
 *
 * Allows the site owner / admin to:
 *  - View current team members and their site access
 *  - Invite new members by email, selecting which sites they can access
 *  - Remove members or revoke pending invitations
 */

import { useEffect, useState } from 'react';
import {
  Users,
  UserPlus,
  Trash2,
  MailCheck,
  Clock,
  Globe,
  ChevronDown,
  ChevronUp,
  Loader2,
  X,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { teamApi, type TeamMember, type PendingInvitation } from '../../lib/api';
import api from '../../lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface OrgSite {
  id: number;
  siteName: string | null;
  siteUrl: string | null;
  platform: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function TeamMembersPage() {
  const { organizations, currentOrganizationId, currentSite } = useAuth();
  // Use the currently active org — prefer currentOrganizationId (set when user switches orgs),
  // fall back to currentSite.organizationId, then first org.
  const orgId = currentOrganizationId
    ?? (currentSite as { organizationId?: number } | null)?.organizationId
    ?? organizations[0]?.id;

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pending, setPending] = useState<PendingInvitation[]>([]);
  const [sites, setSites] = useState<OrgSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([]);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Confirm remove
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);
  const [confirmRemoveType, setConfirmRemoveType] = useState<'member' | 'invite'>('member');
  const [removing, setRemoving] = useState(false);

  // Expanded pending section
  const [pendingExpanded, setPendingExpanded] = useState(true);

  // ---------------------------------------------------------------------------
  // Load members + sites
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!orgId) return;
    loadData();
  }, [orgId]);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [membersRes, sitesRes] = await Promise.all([
        teamApi.getMembers(orgId!),
        api.get(`/api/organizations/${orgId}/sites`),
      ]);
      setMembers(membersRes.members || []);
      setPending(membersRes.pending || []);

      const htmlSites = (sitesRes.data?.sites || []).filter(
        (s: OrgSite) => s.platform !== 'hubspot'
      );
      setSites(htmlSites);

      // Pre-select all sites in the invite form
      if (htmlSites.length > 0 && selectedSiteIds.length === 0) {
        setSelectedSiteIds(htmlSites.map((s: OrgSite) => s.id));
      }
    } catch {
      setError('Failed to load team data. Please refresh.');
    } finally {
      setLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Invite
  // ---------------------------------------------------------------------------
  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    if (!inviteEmail.trim()) {
      setInviteError('Please enter an email address.');
      return;
    }
    if (selectedSiteIds.length === 0) {
      setInviteError('Please select at least one site.');
      return;
    }

    setInviting(true);
    try {
      const res = await teamApi.invite(orgId!, inviteEmail.trim(), selectedSiteIds);
      setInviteSuccess(res.message || `Invitation sent to ${inviteEmail}.`);
      setInviteEmail('');
      setShowInviteForm(false);
      await loadData();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setInviteError(e.response?.data?.error || e.message || 'Failed to send invitation.');
    } finally {
      setInviting(false);
    }
  }

  function toggleSite(siteId: number) {
    setSelectedSiteIds(prev =>
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
  }

  // ---------------------------------------------------------------------------
  // Remove / revoke
  // ---------------------------------------------------------------------------
  async function handleConfirmRemove() {
    if (!confirmRemoveId) return;
    setRemoving(true);
    try {
      if (confirmRemoveType === 'member') {
        await teamApi.removeMember(orgId!, confirmRemoveId);
      } else {
        await teamApi.revokeInvitation(orgId!, confirmRemoveId);
      }
      setConfirmRemoveId(null);
      await loadData();
    } catch {
      // silent
    } finally {
      setRemoving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  function siteName(site: OrgSite) {
    return site.siteName || site.siteUrl || `Site ${site.id}`;
  }

  function siteLabel(id: number) {
    const s = sites.find(s => s.id === id);
    return s ? siteName(s) : `Site ${id}`;
  }

  function formatDate(iso: string | null) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function initials(name: string | null, email: string) {
    if (name && name.trim()) {
      return name.trim().split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
    }
    return email[0].toUpperCase();
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary-400)]" />
      </div>
    );
  }

  if (!orgId) {
    return (
      <div className="p-6 text-center text-[var(--text-secondary)]">No organization found.</div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Users className="w-6 h-6 text-[var(--color-primary-400)]" />
            Team Members
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Invite people to view and work with your site analysis.
          </p>
        </div>
        <button
          onClick={() => { setShowInviteForm(v => !v); setInviteError(''); setInviteSuccess(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
        >
          <UserPlus className="w-4 h-4" />
          Invite Member
        </button>
      </div>

      {/* Global error */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 rounded-xl text-sm text-[var(--color-error)]">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Invite success banner */}
      {inviteSuccess && (
        <div className="flex items-center gap-3 p-4 bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 rounded-xl text-sm text-[var(--color-success)]">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          {inviteSuccess}
        </div>
      )}

      {/* Invite form */}
      {showInviteForm && (
        <div className="glass rounded-2xl p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg">Invite a Team Member</h2>
            <button onClick={() => setShowInviteForm(false)} className="text-white/40 hover:text-white/70">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleInvite} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1.5">Email address</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="colleague@example.com"
                required
                className="w-full px-3 py-2.5 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--color-primary-400)]"
              />
            </div>

            {/* Site selection */}
            {sites.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1.5">
                  Sites they can access
                </label>
                <div className="space-y-2">
                  {sites.map(s => (
                    <label key={s.id} className="flex items-center gap-3 p-3 bg-[var(--bg-tertiary)] rounded-lg cursor-pointer hover:bg-white/5 transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedSiteIds.includes(s.id)}
                        onChange={() => toggleSite(s.id)}
                        className="w-4 h-4 accent-[var(--color-primary-500)]"
                      />
                      <Globe className="w-4 h-4 text-[var(--color-primary-400)] flex-shrink-0" />
                      <span className="text-sm text-white">{siteName(s)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {inviteError && (
              <p className="text-sm text-[var(--color-error)] flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {inviteError}
              </p>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={inviting}
                className="flex-1 py-2.5 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {inviting && <Loader2 className="w-4 h-4 animate-spin" />}
                {inviting ? 'Sending…' : 'Send Invitation'}
              </button>
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="px-4 py-2.5 bg-[var(--bg-tertiary)] text-white/70 text-sm rounded-lg hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Active members */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5">
          <h2 className="font-semibold">Active Members <span className="text-[var(--text-secondary)] font-normal text-sm ml-1">({members.length})</span></h2>
        </div>

        {members.length === 0 ? (
          <div className="px-6 py-10 text-center text-[var(--text-secondary)] text-sm">
            No members yet. Invite someone to get started.
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {members.map(m => (
              <li key={m.id} className="px-6 py-4 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  {/* Avatar */}
                  {m.avatarUrl ? (
                    <img src={m.avatarUrl} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[var(--color-primary-500)]/20 flex items-center justify-center flex-shrink-0 text-sm font-semibold text-[var(--color-primary-400)]">
                      {initials(m.name, m.email)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{m.name || m.email}</span>
                      {m.name && <span className="text-xs text-[var(--text-secondary)]">{m.email}</span>}
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        m.role === 'owner' ? 'bg-amber-500/20 text-amber-400' :
                        m.role === 'admin' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-white/10 text-white/60'
                      }`}>
                        {m.role}
                      </span>
                    </div>
                    {/* Site access */}
                    {m.sites && m.sites.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {m.sites.map(s => (
                          <span key={s.id} className="flex items-center gap-1 text-xs text-white/50 bg-white/5 px-2 py-0.5 rounded-full">
                            <Globe className="w-3 h-3" />
                            {s.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                      Joined {formatDate(m.joinedAt)}
                      {m.lastLoginAt && ` · Last seen ${formatDate(m.lastLoginAt)}`}
                    </p>
                  </div>
                </div>

                {/* Remove button — not shown for owner */}
                {m.role !== 'owner' && (
                  <button
                    onClick={() => { setConfirmRemoveId(m.id); setConfirmRemoveType('member'); }}
                    className="text-white/30 hover:text-[var(--color-error)] transition-colors flex-shrink-0 mt-0.5"
                    title="Remove member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pending invitations */}
      {pending.length > 0 && (
        <div className="glass rounded-2xl overflow-hidden">
          <button
            className="w-full px-6 py-4 border-b border-white/5 flex items-center justify-between"
            onClick={() => setPendingExpanded(v => !v)}
          >
            <h2 className="font-semibold text-left">
              Pending Invitations{' '}
              <span className="text-[var(--text-secondary)] font-normal text-sm ml-1">({pending.length})</span>
            </h2>
            {pendingExpanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
          </button>

          {pendingExpanded && (
            <ul className="divide-y divide-white/5">
              {pending.map(p => (
                <li key={p.id} className="px-6 py-4 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                      <MailCheck className="w-4 h-4 text-white/30" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{p.email}</span>
                        <span className="flex items-center gap-1 text-xs text-amber-400/80 bg-amber-400/10 px-1.5 py-0.5 rounded">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      </div>
                      {p.sites && p.sites.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {p.sites.map(s => (
                            <span key={s.id} className="flex items-center gap-1 text-xs text-white/50 bg-white/5 px-2 py-0.5 rounded-full">
                              <Globe className="w-3 h-3" />
                              {siteLabel(s.id)}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-[var(--text-secondary)] mt-1">
                        Invited {formatDate(p.createdAt)} · Expires {formatDate(p.expiresAt)}
                        {p.invitedByName && ` · by ${p.invitedByName}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setConfirmRemoveId(p.id); setConfirmRemoveType('invite'); }}
                    className="text-white/30 hover:text-[var(--color-error)] transition-colors flex-shrink-0 mt-0.5"
                    title="Revoke invitation"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Confirm remove dialog */}
      {confirmRemoveId !== null && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass rounded-2xl p-6 max-w-sm w-full space-y-4">
            <h3 className="font-semibold text-lg">
              {confirmRemoveType === 'member' ? 'Remove Member?' : 'Revoke Invitation?'}
            </h3>
            <p className="text-sm text-[var(--text-secondary)]">
              {confirmRemoveType === 'member'
                ? 'This person will lose access to all sites in this organization.'
                : 'The invitation email link will no longer work.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmRemove}
                disabled={removing}
                className="flex-1 py-2.5 bg-[var(--color-error)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {removing && <Loader2 className="w-4 h-4 animate-spin" />}
                {confirmRemoveType === 'member' ? 'Remove' : 'Revoke'}
              </button>
              <button
                onClick={() => setConfirmRemoveId(null)}
                className="flex-1 py-2.5 bg-[var(--bg-tertiary)] text-white/70 text-sm rounded-lg hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
